import { useLayoutEffect } from "react";
import { LandmarkIcon } from "lucide-react";
import { StripeIcon } from "@/objects/@icons";

import { Icon } from "@/components/atoms/Icon";
import {
  NavigableCardGrid,
  navigableCardSelectionClassName,
} from "@/components/molecules/NavigableCardGrid";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { State } from "@/state";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const COMMAND_ID = "connect-account-source";

export type ConnectAccountSourceContextType = Record<string, never>;

type Source = {
  id: "bank" | "stripe";
  title: string;
  description: string;
  ReactIcon: typeof LandmarkIcon;
  onClick: () => void;
};

const SOURCES: Source[] = [
  {
    id: "bank",
    title: "Bank account",
    description: "Connect via Plaid or add account numbers manually.",
    ReactIcon: LandmarkIcon,
    onClick: () => {
      State.getState().pushToCommandStack({
        commandDefinitionId: "add-bank-account",
      });
    },
  },
  {
    id: "stripe",
    title: "Stripe",
    description: "Pull charges, payouts, and balance changes from Stripe.",
    ReactIcon: StripeIcon,
    onClick: () => {
      State.getState().pushToCommandStack({
        commandDefinitionId: "connect-stripe-account",
      });
    },
  },
];

function Component() {
  return (
    <StickyLayout>
      <div className="flex flex-col gap-1">
        <Typography
          level="body-sm"
          className="pl-4 pr-4 pt-4 text-muted-foreground"
        >
          Pick a source to connect.
        </Typography>
        <NavigableCardGrid
          itemCount={SOURCES.length}
          minColumnWidth={280}
          getItemKey={(index) => SOURCES[index].id}
          onActivate={(index) => SOURCES[index].onClick()}
          renderItem={({ index, isActive, isFocusWithin, rootProps }) => {
            const source = SOURCES[index];
            return (
              <Card
                {...rootProps}
                className={cn(
                  rootProps.className,
                  "cursor-pointer h-full",
                  navigableCardSelectionClassName({ isActive, isFocusWithin }),
                )}
              >
                <CardHeader>
                  <Icon ReactIcon={source.ReactIcon} size="lg" />
                  <CardTitle>{source.title}</CardTitle>
                  <CardDescription>{source.description}</CardDescription>
                </CardHeader>
              </Card>
            );
          }}
        />
      </div>
    </StickyLayout>
  );
}

export function ConnectAccountSourceCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Connect account",
      description: "Pick a bank or Stripe to connect…",
      getLabel: () => "Connect account",
      component: Component,
      ReactIcon: LandmarkIcon,
      group: "Integrations",
    });
    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
